// Каталог ошибок — дословно из specs/001-service-foundation/contracts/errors.md, новых кодов
// specs/002-platform/contracts/errors.md и таблиц «Ошибки» contracts/api.md 003
// (specs/003-file-to-control-sheet/) и 005 (specs/005-directories/). Пользователь видит причину, следующий шаг, код и номер
// операции (принцип V; FR-027): номер ставит сервер в каждый ответ с ошибкой.
// Кода AUTH-003 нет: ограничение по адресу снято вместе с прежним FR-006.
// Значения настроек (число попыток, длина пароля) в текстах не зашиты — метки {N} заполняет
// renderError() значениями из конфигурации сервера (принцип VIII).
import { z } from '../kernel/zod.ts';
import { formatCount, formatNumber, nouns, plural } from '../text/plural.ts';
import type { PluralForms } from '../text/plural.ts';

export type ErrorHttpStatus = 400 | 401 | 403 | 404 | 409 | 410 | 413 | 422 | 423 | 429 | 503;

export interface ErrorDefinition {
  readonly status: ErrorHttpStatus;
  /** Причина — текст пользователю; метки в фигурных скобках заполняет renderError(). */
  readonly message: string;
  readonly nextStep: string;
}

export const errorCatalog = {
  'SPT-001': {
    status: 413,
    message: 'Диапазон слишком большой',
    nextStep: 'Уменьшите число строк или колонок',
  },
  'SPT-002': {
    status: 409,
    message: 'Этот запрос уже отправлен с другим содержимым',
    nextStep: 'Проверьте предпросмотр и повторите с новым запросом',
  },
  'SPT-003': {
    status: 409,
    message: 'Предпросмотр или сохранённый выбор изменился',
    nextStep: 'Обновите предпросмотр и подтвердите выбор ещё раз',
  },
  'SPT-004': {
    status: 410,
    message: 'Время хранения разбора истекло',
    nextStep: 'Продолжите вручную или запустите разбор заново',
  },
  'SPT-005': {
    status: 429,
    message: 'Слишком много запросов разбора',
    nextStep: 'Подождите или назначьте колонки вручную',
  },
  'SPT-006': {
    status: 422,
    message: 'Не удалось проверить подзадачи или их источник',
    nextStep: 'Проверьте выделенный пункт или выберите «Не разделять»',
  },

  'AUTH-001': {
    status: 401,
    message: 'Почта или пароль не подходят',
    nextStep: 'Проверьте раскладку и попробуйте ещё раз',
  },
  'AUTH-002': {
    status: 423,
    message: 'Вход временно заблокирован после {N} неудачных попыток',
    nextStep: 'Попробуйте через {минут} мин или обратитесь к администратору',
  },
  'AUTH-004': {
    status: 401,
    message: 'Сеанс закончился',
    nextStep: 'Войдите снова — вы вернётесь на тот же экран',
  },
  'AUTH-005': {
    status: 410,
    message: 'Ссылка устарела',
    // 025: новую ссылку пользователь получает сам — «Забыли пароль?» на странице входа.
    nextStep:
      'Откройте ссылку из последнего письма или запросите новую: «Забыли пароль?» на странице входа',
  },
  'AUTH-006': {
    status: 410,
    message: 'Ссылка уже использована',
    nextStep: 'Войдите с паролем или запросите новую ссылку: «Забыли пароль?» на странице входа',
  },
  'AUTH-007': {
    status: 422,
    message: 'Пароль слишком простой',
    nextStep:
      'Введите от {N} знаков с цифрой или символом, не похожий на почту и не из частых, например «Планёрка-2026»',
  },
  'AUTH-008': {
    status: 422,
    message: 'Текущий пароль введён неверно',
    nextStep: 'Введите текущий пароль ещё раз',
  },
  // 022: внешний доступ к протоколам (contracts/api.md 022, «Ошибки»): только после верного пароля.
  'AUTH-009': {
    status: 403,
    message: 'Вход по этой почте выключен администратором',
    nextStep: 'Обратитесь к тому, кто открыл вам доступ к протоколу',
  },
  // 030: вход по ссылке и запасному коду (contracts/api.md 030, «Ошибки»; research Р4).
  'AUTH-010': {
    status: 410,
    message: 'Ссылка больше не действует',
    nextStep: 'Обратитесь к администратору',
  },
  'AUTH-011': {
    status: 400,
    message: 'Код не подходит',
    nextStep: 'Проверьте почту и код из последнего письма или нажмите «Войти по ссылке на почту»',
  },
  // 035 (research Р3): ссылку отменила более новая — письмо или «Скопировать ссылку».
  'AUTH-012': {
    status: 410,
    message: 'Ссылку заменили новой',
    nextStep:
      'Откройте последнее письмо со ссылкой или попросите администратора отправить письмо заново',
  },
  'ACC-001': {
    status: 403,
    message: 'Нет доступа к этому действию',
    nextStep: 'Обратитесь к администратору: {ФИО администраторов}',
  },
  'ACC-002': {
    status: 403,
    message: 'Нет доступа к этой записи',
    nextStep: 'Запись вне ваших протоколов. Обратитесь к администратору: {ФИО администраторов}',
  },
  'ACC-003': {
    status: 403,
    message: 'Нужен временный доступ от администратора',
    nextStep: 'Попросите администратора выдать доступ с причиной: {ФИО администраторов}',
  },
  'ACC-004': {
    status: 409,
    message: 'Нельзя оставить сервис без администратора',
    nextStep: 'Сначала назначьте роль администратора другому пользователю',
  },
  // 020: доступ к протоколу (contracts/api.md 020, «Ошибки»).
  'ACC-005': {
    status: 403,
    message: 'Нет доступа к протоколу',
    nextStep: 'Попросите владельца добавить вас: {ФИО владельца}',
  },
  'ACC-006': {
    status: 429,
    message: 'Приглашений больше 30 за час',
    nextStep: 'Пригласите ещё после {время}',
  },
  // 022: роль на установку внешнему пользователю (research, Р6).
  'ACC-007': {
    status: 409,
    message: 'Роль администратора — только сотрудникам компании',
    nextStep: 'Откройте ему протокол в окне «Доступ»',
  },
  // 030: право «свои пункты» даёт связь учётной записи с человеком (research Р2).
  'ACC-008': {
    status: 403,
    message: 'Учётная запись не связана с человеком справочника',
    nextStep: 'Обратитесь к администратору',
  },
  // 049: неоднозначная или занятая личность не заменяется новым дублем при создании аккаунта.
  'ACC-009': {
    status: 409,
    message: 'Не удалось однозначно связать аккаунт с сотрудником',
    nextStep: 'Проверьте запись сотрудника и его основную почту в справочнике',
  },
  'REC-001': {
    status: 409,
    message: 'Запись изменили, пока она была открыта',
    nextStep: 'Посмотрите, кто и что изменил, и решите, применить ли свою правку',
  },
  'REC-002': {
    status: 404,
    message: 'Запись не найдена',
    nextStep: 'Её перенесли или удалили. Проверьте ссылку',
  },
  'REC-003': {
    status: 409,
    message: 'Эта почта уже используется',
    nextStep: 'Откройте пользователя {ФИО}{ — пользователь в архиве, верните его}',
  },
  'REC-004': {
    status: 422,
    message: 'Почта не на корпоративном домене',
    nextStep: 'Введите адрес на домене {домены}, например ivanov@{домен}',
  },
  // 041 (research Р4): REC-005 и REC-006 — замещение, его больше нет. Коды не переиспользуются.
  'REC-005': {
    status: 409,
    message: 'Замещение пересекается с другим по датам',
    nextStep: 'Выберите другие даты или отмените прежнее замещение',
  },
  'REC-006': {
    status: 422,
    message:
      'Замещение нельзя назначить: {прошедшие даты · сам себе · роль «сопровождение» · роль, полученная по замещению · заместитель — сопровождение · заместитель не активен}',
    nextStep:
      'Выберите другого заместителя, даты или роль. Роль заместителя дальше передаёт администратор',
  },
  'REC-007': {
    status: 422,
    message: 'Форма заполнена с ошибками',
    nextStep: 'Исправьте поля, отмеченные красным',
  },
  'REC-008': {
    status: 413,
    message: 'Слишком большая выгрузка: {N} записей',
    nextStep: 'Сузьте период: до 10 000 записей',
  },
  'REC-009': {
    status: 409,
    message: 'Пользователь в архиве',
    nextStep: 'Сначала верните пользователя из архива',
  },
  // 002: файлы выгрузки журнала в фоне (FR-052б, FR-055).
  'REC-010': {
    status: 410,
    message: 'Файл удалён по сроку хранения',
    nextStep: 'Закажите выгрузку заново',
  },
  'REC-011': {
    status: 409,
    message: 'Файл ещё готовится',
    nextStep: 'Дождитесь уведомления о готовом файле',
  },
  'REC-012': {
    status: 409,
    message: 'У вас уже готовится выгрузка',
    nextStep: 'Дождитесь уведомления о ней — потом закажите следующую',
  },
  'REC-013': {
    status: 413,
    message: 'Слишком большая выгрузка: {N} записей',
    nextStep: 'Сузьте период: до 500 000 записей',
  },
  // 002: фоновые задачи на экране сопровождения (FR-007, FR-008, FR-008а).
  'OPS-001': {
    status: 409,
    message: 'Задача сейчас выполняется — её нельзя отменить',
    nextStep: 'Дождитесь окончания попытки и обновите список',
  },
  'OPS-002': {
    status: 409,
    message: 'Задача уже {состояние}: повторить или отменить её нельзя',
    nextStep: 'Обновите список',
  },
  // Код заказа выгрузки, задачу которого отменило сопровождение (FR-008б): в ответах API его нет.
  'OPS-003': {
    status: 409,
    message: 'Выгрузку отменило сопровождение',
    nextStep: 'Закажите выгрузку снова',
  },
  // «Отменить» в тосте после «Повторить»: задачу уже взял обработчик или её состояние сменилось.
  'OPS-004': {
    status: 409,
    message: 'Повтор уже не отменить: задача {состояние}',
    nextStep: 'Обновите список',
  },
  // 003: загрузка файла протокола и разбор (contracts/api.md 003, «Ошибки»).
  'MGR-001': {
    status: 409,
    message: 'В этой папке уже есть запись с таким названием',
    nextStep: 'Выберите другое название. Существующие данные не изменены',
  },
  'MGR-002': {
    status: 422,
    message: 'Нельзя переместить запись в эту папку',
    nextStep: 'Выберите действующую папку вне перемещаемой ветки',
  },
  'MGR-003': {
    status: 409,
    message: 'Действие недоступно в текущем состоянии корзины',
    nextStep: 'Обновите список и восстановите запись или выберите действующую папку',
  },
  'DOC-001': {
    status: 422,
    message: 'Файл не открывается как книга Excel .xlsx',
    nextStep: 'Сохраните файл в Excel как «Книга Excel (.xlsx)» и загрузите снова',
  },
  'DOC-002': {
    status: 422,
    message: 'В файле нет листа с узнанной шапкой протокола',
    nextStep:
      'Проверьте, что это протокол планёрки. Новую шапку подключает сопровождение — сообщите код и номер операции',
  },
  'DOC-003': {
    status: 413,
    message: 'Файл больше 20 МБ',
    nextStep: 'Удалите из файла старые листы и загрузите снова',
  },
  'DOC-004': {
    status: 409,
    message: 'Лист «{sheet}» не новее загруженного «{last}»',
    nextStep: 'Выберите лист новее или подтвердите загрузку этого листа',
  },
  // 010: архивный лист книги (contracts/api.md 010, «Ошибки»).
  'DOC-005': {
    status: 422,
    message: 'Лист «{sheet}» — архив выполненных, а не лист недели',
    nextStep: 'Выберите лист недели',
  },
  // 011: загрузка версии протокола, который ведётся в системе (contracts/api.md 011, «Ошибки»).
  'DOC-006': {
    status: 409,
    message: 'Протокол «{title}» ведётся в системе — новые версии файла не принимаются',
    nextStep:
      'Правьте протокол в таблице; вернуть режим файла может владелец протокола или администратор',
  },
  'ING-001': {
    status: 409,
    message: 'Документ ещё не разобран',
    nextStep: 'Дождитесь итога разбора на странице «Документы»',
  },
  // 005: справочник, ключи агента, настройки (contracts/api.md 005, «Ошибки»).
  'DIR-001': {
    status: 422,
    message: 'Адрес почты не по формату',
    nextStep: 'Проверьте адрес: имя@домен, без пробелов и запятых',
  },
  'DIR-003': {
    status: 409,
    message: 'У организации не останется основного куратора',
    nextStep: 'Выберите нового основного куратора, затем снимите прежнего',
  },
  'DIR-004': {
    status: 409,
    message: 'Запись склеена с другой или в архиве',
    nextStep: 'Откройте главную запись или верните запись из архива',
  },
  'DIR-005': {
    status: 409,
    message: 'Пара уже решена',
    nextStep: 'Обновите «К разбору»: решённые пары уходят из списка',
  },
  'DIR-006': {
    status: 409,
    message: 'Склейка уже разъединена',
    nextStep: 'Обновите карточку записи',
  },
  // 005-2: смена вида записи (contracts/api.md 005-2, «Ошибки»).
  'DIR-007': {
    status: 409,
    message: 'Свою организацию нельзя сделать человеком',
    nextStep: 'Сначала отметьте организацию внешней',
  },
  'DIR-008': {
    status: 409,
    message: 'Вид записи уже сменён',
    nextStep: 'Откройте запись нового вида по ссылке в карточке',
  },
  'DIR-009': {
    status: 409,
    message: 'Вид уже возвращён',
    nextStep: 'Обновите карточку записи',
  },
  'DIR-010': {
    status: 403,
    message: 'Владелец меняет вид только своих черновиков',
    nextStep: 'Попросите администратора',
  },
  // 006: подразделения и массовые действия (contracts/api.md 006, «Ошибки»).
  'DIR-011': {
    status: 409,
    message: 'Подразделение не может входить само в себя',
    nextStep: 'Выберите родителя вне его вложенных подразделений',
  },
  'DIR-012': {
    status: 409,
    message: 'Такое подразделение уже есть у этого родителя',
    nextStep: 'Откройте существующее или задайте другое название',
  },
  'DIR-013': {
    status: 409,
    message: 'Подразделение в архиве или из другой организации',
    nextStep: 'Верните подразделение из архива или выберите подразделение этой организации',
  },
  'DIR-014': {
    status: 409,
    message: 'У организации есть подразделения — сменить вид или склеить нельзя',
    nextStep: 'Перенесите людей и отправьте подразделения в архив',
  },
  'DIR-015': {
    status: 422,
    message: 'Выбрано больше 200 записей',
    nextStep: 'Выберите записи частями',
  },
  'DIR-016': {
    status: 409,
    message: 'Массовое действие уже отменено',
    nextStep: 'Обновите список',
  },
  // 0.22.5 (ASG-RES-13): «Отменить» после «Не он».
  'DIR-017': {
    status: 409,
    message: 'Отметка «не он» уже отменена',
    nextStep: 'Обновите карточку записи',
  },
  // 037: перевод организации в подразделение (contracts/api.md 037, «Ошибки»).
  'DIR-019': {
    status: 409,
    message: 'Организация есть в протоколах или доступах — перевести её нельзя',
    nextStep: 'Замените её в пунктах и зонах: числа — в окне «Сделать подразделением»',
  },
  'DIR-020': {
    status: 409,
    message: 'Организацию нельзя сделать подразделением самой себя',
    nextStep: 'Выберите другую организацию',
  },
  'DIR-021': {
    status: 409,
    message: 'Организацию уже вернули',
    nextStep: 'Обновите карточку организации',
  },
  // 030: запрос «Проверить связь упоминания» уже решён (contracts/api.md 030, «Ошибки»).
  'DIR-018': {
    status: 409,
    message: 'Запрос уже решён',
    nextStep: 'Обновите «Вопросы»',
  },
  'KEY-001': {
    status: 401,
    message: 'Ключ агента неверный, отозван или истёк',
    nextStep: 'Попросите администратора выдать новый ключ',
  },
  'KEY-002': {
    status: 429,
    message: 'Больше 60 вызовов ключа агента в минуту',
    nextStep: 'Повторите через минуту',
  },
  // 008: канал почты (specs/008-mail-channel/contracts/api.md, «Ошибки»).
  'MAIL-001': {
    status: 409,
    message: 'Почта не подключена',
    nextStep: 'Выберите платформу, введите ключ и отправителя в «Настройки → Почта»',
  },
  'MAIL-002': {
    status: 409,
    message: 'Отправка писем отключена',
    nextStep: 'Включите отправку в «Настройки → Почта»',
  },
  'MAIL-003': {
    status: 422,
    message: 'Это не ключ Resend',
    nextStep: 'Скопируйте ключ из Resend, раздел API Keys: он начинается с re_',
  },
  'MAIL-004': {
    status: 503,
    message: 'Секреты почты не читаются: на сервере не задан или сменился ключ шифрования',
    nextStep:
      'Сопровождению — задать KP_SECRETS_KEY (deploy/README.md), затем ввести ключ Resend заново',
  },
  'MAIL-005': {
    status: 422,
    message: 'Resend не принял ключ: он неверен, удалён или отключён',
    nextStep: 'Создайте ключ в Resend, раздел API Keys, и замените его',
  },
  'MAIL-006': {
    status: 422,
    message: 'Ключ только для отправки: отчёты о доставке и проверка домена недоступны',
    nextStep: 'Создайте в Resend ключ с полным доступом и замените его',
  },
  'MAIL-007': {
    status: 422,
    message: 'Домен отправителя не подтверждён в Resend',
    nextStep: 'Подтвердите домен в Resend, раздел Domains, затем нажмите «Проверить»',
  },
  'MAIL-008': {
    status: 422,
    message: 'Адрес отправителя не на домене из Resend',
    nextStep: 'Укажите адрес на подтверждённом домене, например kontrol@send.example.ru',
  },
  'MAIL-009': {
    status: 429,
    message: 'Предел писем Resend на сутки или месяц исчерпан',
    nextStep:
      'Письма уйдут после сброса предела Resend: суточного — в 00:00 UTC, месячного — 1-го числа; или смените тариф Resend',
  },
  'MAIL-010': {
    status: 503,
    message: 'Resend не отвечает',
    nextStep: 'Письма ждут и уйдут сами; если дольше часа — проверьте status.resend.com',
  },
  'MAIL-011': {
    status: 422,
    message: 'Адрес не годен для писем',
    nextStep: 'Подтвердите адрес или укажите адрес на домене установки',
  },
  'MAIL-012': {
    status: 422,
    message: 'Почтовый сервер адресата отклонил письмо',
    nextStep: 'Проверьте адрес: причина — в карточке письма',
  },
  'MAIL-013': {
    status: 422,
    message: 'Адресат пометил письмо как спам — письма на адрес запрещены',
    nextStep: 'Свяжитесь с адресатом другим способом',
  },
  'MAIL-014': {
    status: 503,
    message: 'Не удалось настроить отчёты о доставке',
    nextStep: 'Нажмите «Настроить отчёты о доставке»; ошибка повторяется — проверьте ключ',
  },
  'MAIL-015': {
    status: 409,
    message: 'Домен отправителя не принимает почту — ответы не дойдут',
    nextStep: 'Добавьте запись MX для приёма в Resend → Domains → Receiving',
  },
  'MAIL-016': {
    status: 429,
    message: 'Пробных писем больше 10 за час',
    nextStep: 'Отправьте пробное письмо после {время}',
  },
  'MAIL-017': {
    status: 422,
    message: 'Resend не принял письмо',
    nextStep: 'Причина — в карточке письма; ошибка повторяется — сообщите сопровождению',
  },
  // 025 (research Р4): причина «ошибки» письма восстановления — ссылка на прежний адрес не уходит.
  'MAIL-018': {
    status: 409,
    message: 'Почта пользователя изменилась после запроса',
    nextStep: 'Пользователь запросит ссылку снова на новую почту',
  },
  // 030: «Первое письмо» (contracts/api.md 030, «Ошибки»; research Р5).
  'MAIL-019': {
    status: 409,
    message: 'Письмо получать некому',
    nextStep: 'Проверьте почту людей в окне',
  },
  'MAIL-020': {
    status: 409,
    message: 'Письма уже ушли — отменить нельзя',
    // В api.md 030 шага нет («—»); каталог показывает шаг всегда — обновить окно.
    nextStep: 'Обновите окно «Первое письмо»',
  },
  // 030 (FR-006, research Р4): письмо со ссылкой входа ждёт, пока канал не выключил отслеживание
  // переходов; проверка подключения выключает его сама.
  'MAIL-021': {
    status: 409,
    message: 'Отслеживание переходов не выключено',
    nextStep: 'Нажмите «Проверить» в «Настройки → Почта»',
  },
  // 033: стоп-кран и рассылка протокола (contracts/api.md 033, «Ошибки»).
  'MAIL-022': {
    status: 409,
    message: 'Все сообщения остановлены',
    nextStep:
      'Нажмите «Возобновить» в «Настройки → Рассылка» — письма уйдут по очереди, пробное отправьте снова',
  },
  'MAIL-023': {
    status: 409,
    message: 'Состояние рассылки протокола изменилось или переход недоступен',
    nextStep: 'Обновите окно; «Включить рассылку» — только из «в тени»',
  },
  // 042: «Шаблоны писем» и согласие на полные письма (contracts/api.md 042, «Ошибки»).
  'MAIL-024': {
    status: 409,
    message: 'Письмо уже изменили',
    nextStep: 'Посмотрите их текст и сохраните свои правки ещё раз',
  },
  'MAIL-025': {
    status: 422,
    message: 'Метки «{метка}» в этом письме нет',
    nextStep: 'Выберите метку из списка или уберите её',
  },
  'MAIL-026': {
    status: 422,
    message: 'Нет метки «{метка}»',
    nextStep: 'Без неё письмо не скажет главного — вставьте её из списка меток',
  },
  'MAIL-027': {
    status: 422,
    message: 'Поле пустое',
    nextStep: 'Напишите текст или нажмите «Вернуть как в системе»',
  },
  // Причина «ошибки» письма: отметку согласия сняли после создания полного письма (FR-031).
  'MAIL-028': {
    status: 409,
    message: 'Нет согласия заказчика на полные письма через этот канал',
    nextStep: 'Отметьте согласие в «Настройки → Почта»; новые письма уйдут извещением',
  },
  'SET-001': {
    status: 422,
    message: 'Значение настройки вне допустимого',
    nextStep: 'Порог пары «тот же?» — число от 0,3 до 0,9; внешняя почта — включено или выключено',
  },
  // 009 (FR-005): убираемый домен — домен почты пользователя не в архиве: тот потерял бы вход.
  'SET-002': {
    status: 409,
    message: 'На домене {домен} есть пользователи: {N}',
    nextStep: 'Смените им почту в «Пользователях» или перенесите их в архив',
  },
  // 010: схемы шаблонов и мастер подключения (contracts/api.md 010, «Ошибки»).
  'TPL-001': {
    status: 422,
    message: 'На листе «{sheet}» не найдена строка шапки',
    nextStep: 'Укажите строку шапки в шаге 1 мастера',
  },
  'TPL-002': {
    status: 422,
    message: 'Не найдена колонка: {field}',
    nextStep: 'Укажите её в шаге 2 мастера: без неё {goal}',
  },
  'TPL-003': {
    status: 409,
    message: 'Схема с этими подписями шапки уже есть: «{title}»',
    nextStep: 'Откройте её в «Настройки → Протоколы»',
  },
  'TPL-004': {
    status: 409,
    message: 'Эту шапку узнаёт схема в архиве: «{title}»',
    nextStep: 'Верните схему из архива в «Настройки → Протоколы»',
  },
  'TPL-005': {
    status: 410,
    message: 'Файл мастера не найден',
    nextStep: 'Выберите файл протокола снова',
  },
  'TPL-006': {
    status: 503,
    message: 'Модель не ответила: {reason}',
    nextStep: 'Ответьте на вопросы мастера сами; модель можно спросить снова позже',
  },
  'TPL-007': {
    status: 422,
    message: 'В файле нет листа с шапкой таблицы',
    nextStep: 'Выберите файл протокола с таблицей пунктов',
  },
  // 011: протокол в системе (contracts/api.md 011, «Ошибки»).
  'MIN-001': {
    status: 409,
    message: 'Протокол «{title}» ведётся по файлу, а не в системе',
    nextStep: 'Переведите протокол в систему на его карточке',
  },
  'MIN-002': {
    status: 409,
    message: 'Протокол «{title}» уже ведётся в системе',
    nextStep: 'Откройте таблицу протокола',
  },
  'MIN-003': {
    status: 422,
    message: 'Последняя версия протокола не готова к переводу: {reason}',
    nextStep: 'Дождитесь разбора или загрузите лист недели заново',
  },
  'MIN-004': {
    status: 409,
    message: 'Команда {commandId} уже выполнена с другими данными',
    nextStep: 'Обновите таблицу и повторите правку',
  },
  'MIN-005': {
    status: 422,
    message: 'Изменение нарушает строение протокола: {reason}',
    nextStep: 'Обновите таблицу; если ошибка повторится — сообщите код сопровождению',
  },
  'MIN-006': {
    status: 422,
    message: 'Поле «{field}» нельзя изменить в таблице',
    nextStep: 'Обновите таблицу',
  },
  // 012: отмена и повтор (contracts/api.md 012, «Ошибки»).
  'MIN-007': {
    status: 409,
    message: 'Действие нельзя отменить: {reason}',
    nextStep: 'Обновите таблицу; отменить можно только своё действие в этой таблице',
  },
  // 013: дополнительные поля (contracts/api.md 013, «Ошибки»).
  'MIN-008': {
    status: 422,
    message: 'В протоколе уже 999 дополнительных полей',
    nextStep: 'Переименуйте ненужное поле вместо нового',
  },
  // 014: протокол без файла и значения полей пункта (contracts/api.md 014, «Ошибки»).
  'MIN-009': {
    status: 409,
    message: 'Протокол создан в системе — файла нет',
    nextStep: 'Протокол ведётся только в таблице',
  },
  'MIN-010': {
    status: 422,
    message: 'Значение не принято: {reason}',
    nextStep:
      'Исправьте срок, выберите состояние из списка, выберите действующую запись или пользователя',
  },
  // 016: снимок недели и печать ветви (contracts/api.md, «Ошибки»).
  'MIN-011': {
    status: 404,
    message: 'Снимок недели не найден в этом протоколе',
    nextStep: 'Откройте вкладку «Недели» и выберите снимок заново',
  },
  'MIN-012': {
    status: 422,
    message: 'Ветви для печати больше нет в протоколе',
    nextStep: 'Выберите область печати заново',
  },
  // 017: объединение ячеек (contracts/api.md 017).
  'MIN-013': {
    status: 422,
    message: 'Объединение не прямоугольное или пересекает другое',
    nextStep: 'Выделите ячейки заново и повторите',
  },
  'MIN-014': {
    status: 403,
    message: 'Пункт вне вашей зоны: {пункты}',
    nextStep:
      'Править можно только пункты своей зоны. Остальные правят владелец и редакторы протокола',
  },
  'MIN-015': {
    status: 403,
    message: 'Пункт выйдет из вашей зоны: {пункты}',
    nextStep: 'Сначала укажите ответственного из вашей зоны',
  },
  'MIN-016': {
    status: 422,
    message: 'Доступ не выдан: {причина}',
    nextStep: 'Выберите другого сотрудника или смените роль в списке',
  },
  'MIN-017': {
    status: 422,
    message: 'Укажите ФИО: адреса нет в справочнике',
    nextStep: 'Введите ФИО сотрудника, например Новиков Н. Н.',
  },
  'MIN-018': {
    status: 422,
    message: 'Запись зоны не найдена или в архиве',
    nextStep: 'Выберите действующую организацию или подразделение',
  },
  // 027: ответственный — человек, контрагент — организация (FR-001, FR-003). Без имени записи:
  // текст ошибки попадает в технический журнал (принцип XIV).
  'MIN-019': {
    status: 422,
    message: 'Запись не того вида: ответственный — человек, контрагент — организация',
    nextStep: 'Выберите действующую запись нужного вида или перенесите её в другую колонку',
  },
  // 029: история версий (contracts/api.md 029, «Ошибки»). У MIN-021 в контракте шага нет — окно
  // пишет «Протокол уже такой» или «Выделенное уже такое»; для прочих мест показа шаг — свой.
  'MIN-020': {
    status: 409,
    message: 'Протокол изменили, пока окно было открыто',
    nextStep: 'Проверьте новые числа и подтвердите ещё раз',
  },
  'MIN-021': {
    status: 409,
    message: 'Менять нечего: {reason}',
    nextStep: 'Выберите другую версию или другое выделение',
  },
  'MIN-022': {
    status: 404,
    message: 'Версии нет в истории протокола',
    nextStep: 'Обновите список версий',
  },
  // 030: право «свои пункты» — команда с чужим пунктом отклоняется целиком (research Р2).
  'MIN-023': {
    status: 403,
    message: 'Команда затрагивает чужой пункт: {пункты}',
    nextStep: 'Отметьте только свои пункты или попросите владельца протокола',
  },
  // 032 (contracts/api.md 032, §3): ответ из письма уже внёс другой контролёр.
  'MIN-024': {
    status: 409,
    message: 'Ответ уже внесён',
    nextStep: 'Обновите карточку: ответ внёс другой пользователь',
  },
  // 038: окно «Назначить ведущего» (contracts/api.md 038, «Ошибки»; research Р4, Р8).
  'MIN-025': {
    status: 409,
    message: 'Владельца уже сменили: {сейчас}',
    nextStep: 'Проверьте, кого назначить, и выберите снова',
  },
  'MIN-026': {
    status: 409,
    message: 'У человека из справочника уже есть учётная запись: {ФИО}',
    nextStep: 'Найдите его в разделе «Пользователи» и назначьте',
  },
  'SYS-001': {
    status: 503,
    message: 'Сервис временно недоступен',
    nextStep: 'Данные в сохранности. Повторите через минуту',
  },
  'SYS-002': {
    status: 403,
    message: 'Запрос отклонён: неверный источник',
    nextStep: 'Обновите страницу и повторите действие',
  },
} as const satisfies Record<string, ErrorDefinition>;

export type ErrorCode = keyof typeof errorCatalog;

export const errorCodes = Object.keys(errorCatalog) as ErrorCode[];

export const ErrorCodeSchema = z.enum(errorCodes);

export function isErrorCode(value: string): value is ErrorCode {
  return Object.hasOwn(errorCatalog, value);
}

export function errorStatus(code: ErrorCode): ErrorHttpStatus {
  return errorCatalog[code].status;
}

/**
 * Коды интерфейса (contracts/errors.md, «Коды интерфейса»): ответа сервиса нет — запрос не
 * дошёл или действие не выполнил браузер. Их показывает рабочее приложение; в ответах API
 * и в ErrorCodeSchema их нет.
 */
export const clientErrorCatalog = {
  /** Обрыв связи (FR-040): ошибка списка, экрана и тост несохранённого изменения. */
  'NET-001': {
    message: 'Нет связи с сервисом',
    nextStep: 'Проверьте подключение и повторите',
  },
  /**
   * Браузер не дал записать в буфер обмена (T147): «Скопировать ссылку» в окне со
   * ссылкой-приглашением и «Скопировать ссылку на карточку». Ссылка в окне выделяется.
   */
  'CLIP-001': {
    message: 'Не удалось скопировать ссылку',
    nextStep: 'Скопируйте её вручную',
  },
  // 013: буфер ячеек таблицы протокола (plan.md 013, «Толкования после сверки»).
  'CLIP-002': {
    message: 'В буфере нет таблицы или текста',
    nextStep: 'Скопируйте ячейки ещё раз',
  },
  'CLIP-003': {
    message: 'Таблицу из буфера не разобрать',
    nextStep: 'Скопируйте диапазон без вложенных таблиц и перекрытых объединений',
  },
  'CLIP-004': {
    message: 'Таблица изменилась — вставка отменена',
    nextStep: 'Вставьте ещё раз',
  },
  'CLIP-005': {
    message: 'Вставка в несколько диапазонов недоступна',
    nextStep: 'Выделите одну ячейку или один диапазон',
  },
  'CLIP-006': {
    message: 'Размер выделения не кратен буферу',
    nextStep: 'Выделите диапазон, кратный скопированному, или одну ячейку',
  },
  // Шаг без клавиш: на телефоне вставка есть только через меню (013: сходимость, T050).
  'CLIP-007': {
    message: 'Браузер не дал прочитать буфер',
    nextStep: 'Разрешите сайту доступ к буферу обмена в настройках браузера и повторите',
  },
  'CLIP-008': {
    message: 'Не удалось скопировать ячейки',
    nextStep: 'Разрешите сайту доступ к буферу обмена в настройках браузера и повторите',
  },
} as const satisfies Record<string, Omit<ErrorDefinition, 'status'>>;

export type ClientErrorCode = keyof typeof clientErrorCatalog;

/**
 * CLIP-001 у кнопки «Скопировать номер» (002, contracts/errors.md, «Коды интерфейса»): номер
 * операции выделяется, его можно скопировать вручную.
 */
export const operationCopyFailure = {
  message: 'Не удалось скопировать',
  nextStep: 'Выделите номер и скопируйте вручную',
} as const satisfies Omit<ErrorDefinition, 'status'>;

/** Причины REC-006 в шаблоне причины — в том же порядке, что в errors.md. */
export const substitutionRejectReasons = {
  past_dates: 'прошедшие даты',
  self: 'сам себе',
  support_role: 'роль «сопровождение»',
  substituted_role: 'роль, полученная по замещению',
  /** Заместитель — пользователь с ролью «сопровождение» (FR-018а). */
  deputy_support: 'заместитель — сопровождение',
  /** Заместитель в архиве или без заданного пароля (FR-018б). */
  deputy_inactive: 'заместитель не активен',
} as const;

/**
 * Отказы REC-006 со своей причиной и шагом (errors.md, таблица «Причина REC-006»): смена роли
 * назначения при его действующих или запланированных замещениях — не выбор заместителя, и шаг
 * у неё другой (T076, T123).
 */
export const substitutionRejectTexts = {
  role_change: {
    message: 'Роль нельзя сменить: у неё есть действующие или запланированные замещения',
    nextStep: 'Сначала отмените замещения этой роли',
  },
  /**
   * Роль «сопровождение» тому, кто замещает или будет замещать (FR-018а; T111): администратор
   * выдаёт роль, а не выбирает заместителя, — причина и шаг свои.
   */
  support_for_deputy: {
    message: 'Роль «сопровождение» нельзя выдать: пользователь замещает чужую роль',
    nextStep: 'Сначала отмените замещения, где пользователь — заместитель',
  },
} as const satisfies Record<string, { readonly message: string; readonly nextStep: string }>;

export type SubstitutionRejectReason =
  keyof typeof substitutionRejectReasons | keyof typeof substitutionRejectTexts;

function hasOwnRejectText(
  reason: SubstitutionRejectReason,
): reason is keyof typeof substitutionRejectTexts {
  return Object.hasOwn(substitutionRejectTexts, reason);
}

/**
 * Состояние задачи в OPS-002 (contracts/errors.md 002): «Задача уже выполнена: повторить или
 * отменить её нельзя». Значения — состояния kernel.job_state; running — у «Повторить»
 * выполняющейся задачи (у «Отменить» — OPS-001).
 */
export const jobStateInError = {
  done: 'выполнена',
  cancelled: 'отменена',
  queued: 'ждёт выполнения',
  running: 'выполняется',
} as const;

export type JobStateInError = keyof typeof jobStateInError;

/**
 * Состояние задачи в OPS-004 (contracts/errors.md 002): «Повтор уже не отменить: задача
 * выполняется». queued — задачу уже брал обработчик, попытка не удалась, и она снова ждёт.
 */
export const retryUndoStateInError = {
  running: 'выполняется',
  queued: 'выполнялась и ждёт следующей попытки',
  failed: 'упала',
  done: 'выполнена',
  cancelled: 'отменена',
} as const;

export type RetryUndoStateInError = keyof typeof retryUndoStateInError;

/** Значения для меток ошибки. Коды без меток значений не принимают. */
export interface ErrorParams {
  /**
   * minutes — минут до конца блокировки, целое; failures — после скольких неудач подряд
   * блокируется вход (настройка auth.lockout.maxFailures).
   */
  'AUTH-002': { readonly minutes: number; readonly failures: number };
  /** Не короче скольких знаков пароль (настройка auth.password.minLength). */
  'AUTH-007': { readonly minLength: number };
  /** ФИО активных администраторов клиента (FR-016). */
  'ACC-001': { readonly admins: readonly string[] };
  'ACC-002': { readonly admins: readonly string[] };
  'ACC-003': { readonly admins: readonly string[] };
  /** Пользователь, у которого уже эта почта. */
  'REC-003': { readonly fullName: string; readonly archived: boolean };
  /** Корпоративные домены; первый идёт в пример. */
  'REC-004': { readonly domains: readonly [string, ...string[]] };
  'REC-006': { readonly reason: SubstitutionRejectReason };
  /** Сколько записей попало бы в выгрузку. */
  'REC-008': { readonly count: number };
  /** Сколько записей попало бы в выгрузку в фоне (больше 500 000, FR-052б). */
  'REC-013': { readonly count: number };
  /** Текущее состояние задачи: повтор и отмена ему не подходят (FR-008а). */
  'OPS-002': { readonly state: JobStateInError };
  /** Текущее состояние задачи, повтор которой уже не отменить. */
  'OPS-004': { readonly state: RetryUndoStateInError };
  /** Выбранный лист и лист последней загруженной версии документа (FR-027). */
  'DOC-004': { readonly sheet: string; readonly last: string };
  /** Время, когда можно снова отправить пробное письмо: «14:05» (008, FR-020). */
  'MAIL-016': { readonly at: string };
  /** Убираемый домен и сколько на нём пользователей не в архиве (009, FR-005). */
  'SET-002': { readonly domain: string; readonly count: number };
  /** Архивный лист, выбранный листом недели (010, FR-042). */
  'DOC-005': { readonly sheet: string };
  /** Лист без строки шапки (010, FR-006). */
  'TPL-001': { readonly sheet: string };
  /** Не найденное обязательное поле и зачем оно: «Срок», «нет статусов по датам» (010, FR-015). */
  'TPL-002': { readonly field: string; readonly goal: string };
  /** Схема с теми же подписями — действующая или в архиве (010, FR-030, FR-036). */
  'TPL-003': { readonly title: string };
  'TPL-004': { readonly title: string };
  /** Почему модель не ответила: «нет ключа», «срок ответа», «ответ не по схеме» (010, FR-052). */
  'TPL-006': { readonly reason: string };
  /** Протокол, который ведётся в системе (011, FR-013). */
  'DOC-006': { readonly title: string };
  /** Название протокола (011, MIN-001, MIN-002). */
  'MIN-001': { readonly title: string };
  'MIN-002': { readonly title: string };
  /** Почему версия не готова к переводу: «идёт разбор», «разбор не удался» (011, FR-003). */
  'MIN-003': { readonly reason: string };
  /** Номер команды клиента (011, FR-035). */
  'MIN-004': { readonly commandId: string };
  /** Какой инвариант дерева нарушен: «цикл», «родитель не из протокола» (011, FR-037). */
  'MIN-005': { readonly reason: string };
  /** Поле команды (011, FR-030). */
  'MIN-006': { readonly field: string };
  /** Почему действие нельзя отменить (012, MIN-007): без имён и номеров чужих действий. */
  'MIN-007': { readonly reason: string };
  /** Какое значение не принято (014, MIN-010): причина ядра, без значений других полей. */
  'MIN-010': { readonly reason: string };
  /** ФИО владельца протокола; протокол без ведущего — null (020, ACC-005). */
  'ACC-005': { readonly owner: string | null };
  /** Время, когда можно снова пригласить: «14:05» (020, ACC-006). */
  'ACC-006': { readonly at: string };
  /** Номера пунктов вне зоны (020, MIN-014, MIN-015) — без названий. */
  'MIN-014': { readonly items: readonly string[] };
  'MIN-015': { readonly items: readonly string[] };
  /** Почему роль в протоколе не выдаётся (020, MIN-016). */
  'MIN-016': { readonly reason: GrantRejectReason };
  /** Почему менять нечего (029, MIN-021): «протокол уже такой, как в версии», «выделенное…». */
  'MIN-021': { readonly reason: string };
  /** Номера чужих пунктов команды «своих пунктов» (030, MIN-023) — без названий. */
  'MIN-023': { readonly items: readonly string[] };
  /** Нынешний ведущий (038, MIN-025); null — ведущего сняли. */
  'MIN-025': { readonly name: string | null };
  /** Пользователь, с которым уже связан человек справочника (038, MIN-026). */
  'MIN-026': { readonly fullName: string };
  /** Метка, которой нет у записи каталога или которую убрали (042, MAIL-025, MAIL-026). */
  'MAIL-025': { readonly mark: string };
  'MAIL-026': { readonly mark: string };
}

/** Почему роль в протоколе не выдаётся (020: contracts/api.md, MIN-016). */
export const grantRejectReasons = {
  owner: 'это владелец протокола',
  support: 'сопровождение видит протоколы только по временному доступу',
  archived: 'пользователь в архиве',
  already: 'доступ уже есть',
} as const;
export type GrantRejectReason = keyof typeof grantRejectReasons;

/** Сколько номеров пунктов называет отказ зоны: остальные — «и ещё N». */
export const ZONE_DENIAL_ITEMS = 5;

type ParamsArg<C extends ErrorCode> = C extends keyof ErrorParams ? [params: ErrorParams[C]] : [];

export interface RenderedError<C extends ErrorCode = ErrorCode> {
  readonly code: C;
  readonly message: string;
  readonly nextStep: string;
}

interface Texts {
  message: string;
  nextStep: string;
}

const adminsText = (t: Texts, p: ErrorParams['ACC-001']): Texts => ({
  message: t.message,
  nextStep:
    p.admins.length > 0
      ? t.nextStep.replace('{ФИО администраторов}', p.admins.join(', '))
      : t.nextStep.replace(': {ФИО администраторов}', ''),
});

/** «после 1 неудачной попытки», «после 5 неудачных попыток» — родительный падеж после «после». */
const failedAttemptsAfter: PluralForms = [
  'неудачной попытки',
  'неудачных попыток',
  'неудачных попыток',
];
/** «не меньше 1 знака», «не меньше 10 знаков». */
const charactersAtLeast: PluralForms = ['знака', 'знаков', 'знаков'];

const countText = (n: number, forms: PluralForms): string =>
  `${formatNumber(n)} ${plural(n, forms)}`;

const renderers: { readonly [C in keyof ErrorParams]: (t: Texts, p: ErrorParams[C]) => Texts } = {
  'AUTH-002': (t, p) => ({
    message: t.message.replace('{N} неудачных попыток', countText(p.failures, failedAttemptsAfter)),
    nextStep: t.nextStep.replace('{минут}', String(p.minutes)),
  }),
  'AUTH-007': (t, p) => ({
    ...t,
    nextStep: t.nextStep.replace('{N} знаков', countText(p.minLength, charactersAtLeast)),
  }),
  'ACC-001': adminsText,
  'ACC-002': adminsText,
  'ACC-003': adminsText,
  'REC-003': (t, p) => ({
    ...t,
    nextStep: t.nextStep
      .replace('{ФИО}', p.fullName)
      .replace(
        '{ — пользователь в архиве, верните его}',
        p.archived ? ' — пользователь в архиве, верните его' : '',
      ),
  }),
  'REC-004': (t, p) => ({
    ...t,
    nextStep: t.nextStep.replace('{домены}', p.domains.join(', ')).replace('{домен}', p.domains[0]),
  }),
  'REC-006': (t, p) =>
    hasOwnRejectText(p.reason)
      ? { ...substitutionRejectTexts[p.reason] }
      : { ...t, message: t.message.replace(/\{[^}]*\}/, substitutionRejectReasons[p.reason]) },
  // Число согласуется со словом: «10 001 запись», «12 345 записей» (FR-036в).
  'REC-008': (t, p) => ({
    ...t,
    message: t.message.replace('{N} записей', formatCount(p.count, nouns.record)),
  }),
  'REC-013': (t, p) => ({
    ...t,
    message: t.message.replace('{N} записей', formatCount(p.count, nouns.record)),
  }),
  'OPS-002': (t, p) => ({
    ...t,
    message: t.message.replace('{состояние}', jobStateInError[p.state]),
  }),
  'OPS-004': (t, p) => ({
    ...t,
    message: t.message.replace('{состояние}', retryUndoStateInError[p.state]),
  }),
  'DOC-004': (t, p) => ({
    ...t,
    message: t.message.replace('{sheet}', p.sheet).replace('{last}', p.last),
  }),
  'MAIL-016': (t, p) => ({ ...t, nextStep: t.nextStep.replace('{время}', p.at) }),
  'SET-002': (t, p) => ({
    ...t,
    message: t.message.replace('{домен}', p.domain).replace('{N}', formatNumber(p.count)),
  }),
  'DOC-005': (t, p) => ({ ...t, message: t.message.replace('{sheet}', p.sheet) }),
  'TPL-001': (t, p) => ({ ...t, message: t.message.replace('{sheet}', p.sheet) }),
  'TPL-002': (t, p) => ({
    message: t.message.replace('{field}', p.field),
    nextStep: t.nextStep.replace('{goal}', p.goal),
  }),
  'TPL-003': (t, p) => ({ ...t, message: t.message.replace('{title}', p.title) }),
  'TPL-004': (t, p) => ({ ...t, message: t.message.replace('{title}', p.title) }),
  'TPL-006': (t, p) => ({ ...t, message: t.message.replace('{reason}', p.reason) }),
  // Название протокола — текст пользователя: «$&» в нём не должен стать подстановкой (ревью 011).
  'DOC-006': (t, p) => ({ ...t, message: t.message.replace('{title}', () => p.title) }),
  'MIN-001': (t, p) => ({ ...t, message: t.message.replace('{title}', () => p.title) }),
  'MIN-002': (t, p) => ({ ...t, message: t.message.replace('{title}', () => p.title) }),
  'MIN-003': (t, p) => ({ ...t, message: t.message.replace('{reason}', p.reason) }),
  'MIN-004': (t, p) => ({ ...t, message: t.message.replace('{commandId}', p.commandId) }),
  'MIN-005': (t, p) => ({ ...t, message: t.message.replace('{reason}', p.reason) }),
  'MIN-006': (t, p) => ({ ...t, message: t.message.replace('{field}', p.field) }),
  'MIN-007': (t, p) => ({ ...t, message: t.message.replace('{reason}', () => p.reason) }),
  'MIN-010': (t, p) => ({ ...t, message: t.message.replace('{reason}', () => p.reason) }),
  'ACC-005': (t, p) => ({
    message: t.message,
    nextStep:
      p.owner === null
        ? 'Попросите администратора добавить вас'
        : t.nextStep.replace('{ФИО владельца}', () => p.owner ?? ''),
  }),
  'ACC-006': (t, p) => ({ ...t, nextStep: t.nextStep.replace('{время}', p.at) }),
  'MIN-014': (t, p) => ({ ...t, message: t.message.replace('{пункты}', () => itemsText(p.items)) }),
  'MIN-015': (t, p) => ({ ...t, message: t.message.replace('{пункты}', () => itemsText(p.items)) }),
  'MIN-016': (t, p) => ({
    ...t,
    message: t.message.replace('{причина}', grantRejectReasons[p.reason]),
  }),
  'MIN-021': (t, p) => ({ ...t, message: t.message.replace('{reason}', () => p.reason) }),
  'MIN-023': (t, p) => ({ ...t, message: t.message.replace('{пункты}', () => itemsText(p.items)) }),
  'MIN-025': (t, p) => ({
    ...t,
    message: t.message.replace('{сейчас}', () =>
      p.name === null ? 'владельца сняли' : `сейчас ${p.name}`,
    ),
  }),
  'MIN-026': (t, p) => ({ ...t, message: t.message.replace('{ФИО}', () => p.fullName) }),
  // Метка — текст администратора: «$&» в нём не должен стать подстановкой.
  'MAIL-025': (t, p) => ({ ...t, message: t.message.replace('{метка}', () => p.mark) }),
  'MAIL-026': (t, p) => ({ ...t, message: t.message.replace('{метка}', () => p.mark) }),
};

/** «3.2, 4.1, 5 и ещё 2» — номера пунктов отказа зоны (020). */
function itemsText(items: readonly string[]): string {
  const shown = items.slice(0, ZONE_DENIAL_ITEMS).join(', ');
  const rest = items.length - ZONE_DENIAL_ITEMS;
  return rest > 0 ? `${shown} и ещё ${formatNumber(rest)}` : shown;
}

/** Текст ошибки для ответа: причина и следующий шаг с заполненными метками. */
export function renderError<C extends ErrorCode>(code: C, ...args: ParamsArg<C>): RenderedError<C> {
  const def: ErrorDefinition = errorCatalog[code];
  const texts: Texts = { message: def.message, nextStep: def.nextStep };
  const render = (renderers as Partial<Record<ErrorCode, (t: Texts, p: unknown) => Texts>>)[code];
  const filled = render && args.length > 0 ? render(texts, args[0]) : texts;
  return { code, message: filled.message, nextStep: filled.nextStep };
}
